<template>
  <!--
    No title bar. Matt: "let's get rid of the header bar that says
    Commonwealth, because that's not really useful... let's reclaim that
    space." What it carried - the code, the ?, leaving - is behind this one
    button, which floats clear of the layout so the boards start at the top.
  -->
  <button
    v-if="isApproved"
    class="app-menu-btn"
    type="button"
    title="Menu"
    aria-label="Menu"
    @click="showMenu = true"
  >
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
      <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />
    </svg>
  </button>

  <!-- The TV goes full screen in one tap (playtest 12, Matt: "I would love
       to have a full screen button for the screen on my TV"). -->
  <button
    v-if="isApproved && isTable && canFullscreen"
    class="app-menu-btn app-menu-btn--fullscreen"
    type="button"
    :title="fullscreen ? 'Leave full screen' : 'Full screen'"
    :aria-label="fullscreen ? 'Leave full screen' : 'Full screen'"
    @click="toggleFullscreen"
  >
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path v-if="!fullscreen" d="M2.5 6V2.5H6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10" />
      <path v-else d="M6 2.5V6H2.5M13.5 6H10V2.5M10 13.5V10h3.5M2.5 10H6v3.5" />
    </svg>
  </button>

  <AppMenu
    v-if="showMenu"
    :room-code="roomMeta?.roomCode || ''"
    :is-table="isTable"
    :is-admin="isAdmin"
    :can-leave="Boolean(game || roomId)"
    :leave-label="leaveLabel"
    :playtest="roomId && !isTable ? playtest : null"
    @playtest-toggle="playtestSwitch"
    @mic-toggle="micToggle"
    @close="showMenu = false"
    @help="showMenu = false; openHelp()"
    @leave="showMenu = false; confirmLeave()"
  />

  <!-- Playtest mode: this phone is sending what it hears to Claude. Tap to stop. -->
  <!-- Stopping takes TWO taps (playtest 13, Matt: "my mic keeps getting
       turned off, maybe I'm bumping it"); a bump shows "tap again to stop"
       for a moment and does nothing. -->
  <button v-if="playtest.mic === 'on' && !isTable" class="playtest-pill" :class="{ 'playtest-pill--arm': micArmed }" type="button" @click="micToggle">{{ micArmed ? 'tap again to stop the mic' : `● rec ${playtest.sent}` }}</button>
  <button v-else-if="playtest.mic === 'paused' && roomId && !isTable" class="playtest-pill playtest-pill--ask playtest-pill--paused" type="button" @click="micToggle">○ YOUR MIC IS PAUSED - tap anywhere to resume</button>
  <button v-else-if="playtest.on && roomId && !isTable" class="playtest-pill playtest-pill--ask" type="button" @click="micToggle">○ tap for mic</button>

  <!-- Whether Claude is listening (the worker's heartbeat), on every screen while playtest mode is on. -->
  <div v-if="playtest.on && roomId" class="listener-slip" :class="[`listener-slip--${listenerStatus().tone}`, { 'listener-slip--table': isTable }]" role="status">{{ listenerStatus().text }}</div>

  <!-- Claude answering the table (yarn playtest:say). On the TV only (Matt,
       playtest 11: "I just have that show up on the TV really, if at all"). -->
  <div v-if="playtest.note && isTable" class="claude-note" :class="{ 'claude-note--table': isTable }" role="status" @click="playtest.note = null">
    <strong>Claude</strong> {{ playtest.note.text }}
  </div>

  <SiteGate v-if="!isApproved" />
  <RoomChoice v-else-if="pendingChoice" :code="pendingChoice" @pick="chooseScreen" />
  <TableView v-else-if="game && isTable" />
  <PhoneView v-else-if="game" />
  <Lobby v-else-if="roomId || wantsOnline" @back="wantsOnline = false" />
  <Landing v-else @play-online="wantsOnline = true" />

  <!--
    One door for help, opened by the ? in the header. In a game it offers
    your own role's how-to, the game's, and the words; outside a game the
    game's walkthrough is the only thing there is, so it opens straight.
  -->
  <HelpMenu v-if="helpView === 'menu'" :role="help.role" @pick="helpPick" @close="helpView = null" />
  <HowToPlay v-if="showHowTo || helpView === 'game'" @close="dismissHowTo" />
  <RoleGuide v-if="helpView === 'role' && help.role" :role="help.role" @close="helpView = null" />
  <Glossary v-if="helpView === 'words'" @close="helpView = null" />
  <Victories v-if="helpView === 'victories'" :civ="game?.civ || null" @close="helpView = null" />
  <!-- The practice day over the top of everything (src/tutorial). -->
  <TutorialCoach v-if="tutorial" />

  <!--
    Tap the stamp for what the screen actually measures. Three rounds of
    "there is dead space at the bottom" were guesswork because nobody could
    read the numbers off the phone in the room. Now anyone can.
  -->
  <!-- Tap reloads the app (no URL bar in an installed PWA); press and hold
       is the viewport measurement, which is a debugging tool and not the
       thing a tap should do. -->
  <p
    class="build-stamp"
    role="button"
    tabindex="0"
    title="Tap to reload · hold to measure"
    @click="stampTap"
    @pointerdown="stampHold"
    @pointerup="stampRelease"
    @pointerleave="stampRelease"
    @pointercancel="stampRelease"
  >{{ measured || (refreshing ? 'reloading…' : stamp) }}</p>
  <BugReportButton v-if="firebaseConfigured && !isTable" />
  <BugResolutionNotice v-if="firebaseConfigured && !isTable" />
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { playtest, bindPlaytest, toggleMic, setPlaytestOn, setMicContext, listenerStatus } from '@/utils/playtest';
import { reloadIfNewer } from '@/utils/appUpdate';
import { help as helpState } from '@/help';
import TableView from '@/components/game/TableView.vue';
import PhoneView from '@/components/game/PhoneView.vue';
import Landing from '@/components/lobby/Landing.vue';
import Lobby from '@/components/lobby/Lobby.vue';
import RoomChoice from '@/components/lobby/RoomChoice.vue';
import SiteGate from '@/components/auth/SiteGate.vue';
import HowToPlay from '@/components/shared/HowToPlay.vue';
import AppMenu from '@/components/shared/AppMenu.vue';
import HelpMenu from '@/components/shared/HelpMenu.vue';
import RoleGuide from '@/components/shared/RoleGuide.vue';
import Glossary from '@/components/shared/Glossary.vue';
import Victories from '@/components/shared/Victories.vue';
import TutorialCoach from '@/components/shared/TutorialCoach.vue';
import { help } from '@/help';
import BugReportButton from '@/components/shared/BugReportButton.vue';
import BugResolutionNotice from '@/components/shared/BugResolutionNotice.vue';
import { buildStamp, forceRefresh } from '@/utils/buildStamp';
import { initUpdateCheck } from '@/utils/checkForUpdate';
import { previewGame } from '@/dev/preview';
import { viewportLine } from '@/utils/viewport';

const HOWTO_SEEN_KEY = 'commonwealth:howto-seen';

export default {
  name: 'App',
  components: {
    TableView, PhoneView, Landing, Lobby, RoomChoice, SiteGate, BugReportButton, BugResolutionNotice, HowToPlay,
    HelpMenu, RoleGuide, Glossary, Victories, AppMenu, TutorialCoach,
  },
  data() {
    return {
      micArmed: false,
      canFullscreen: Boolean(document.fullscreenEnabled),
      fullscreen: Boolean(document.fullscreenElement),
      stamp: buildStamp(),
      playtest,
      listenerStatus,
      wantsOnline: false,
      pendingJoin: null,
      // ?room=FROG: the one shared link, waiting to be told which screen this is.
      pendingChoice: null,
      showHowTo: false,
      showMenu: false,
      helpView: null,
      help,
      previewing: false,
      measured: '',
      refreshing: false,
      held: false,
    };
  },
  computed: {
    ...mapState(['game', 'roomId', 'roomMeta', 'firebaseConfigured', 'tutorial']),
    ...mapGetters(['isApproved', 'isAdmin', 'isTable']),
    rememberedRoom() {
      try { return Boolean(JSON.parse(localStorage.getItem('commonwealth:room') || 'null')?.id); } catch { return false; }
    },
    leaveLabel() {
      if (this.isTable) return 'Close the table';
      return this.roomId ? 'Leave this game' : 'Abandon this game';
    },
  },
  watch: {
    // A new version waiting is taken at DAWN, the natural pause between
    // rounds (Matt, 2026-09-25: "it would be nice if the reloads happened
    // automatically when a new version came out so I didn't have to hit
    // that button"). The house checker only reloads after 25s untouched,
    // which a game in play never gives it.
    'game.meta.phase': function onPhase(now, was) {
      if (now !== 'dawn' || was === 'dawn' || !was) return;
      reloadIfNewer();
    },
    // Playtest mode follows whichever room this screen is in.
    roomId: {
      immediate: true,
      handler(id) { bindPlaytest(id || null); },
    },
    isApproved(approved) {
      if (approved) this.consumeJoin();
    },
  },
  created() {
    // ?join=FROG drops a phone into the room; ?table=FROG opens the big
    // screen on it. Either way, no code to re-type.
    const params = new URLSearchParams(window.location.search);
    const join = params.get('join');
    const table = params.get('table');
    const room = params.get('room');
    // Dev only: ?preview=table&at=mid puts a scripted game on the table so
    // the world can be tuned without a room; ?preview=phone does the same
    // for a phone screen. See src/dev/preview.js.
    const preview = params.get('preview');
    if (import.meta.env.DEV && (preview === 'table' || preview === 'phone')) {
      this.previewing = true;
      this.$store.commit('setSiteUser', { status: 'approved' });
      this.$store.commit('setMode', preview);
      this.$store.commit('setRemoteGame', previewGame(params.get('at'), { only: params.get('only') }));
      // So a console (or Claude's screenshot loop) can poke the world.
      window.__preview = { store: this.$store, previewGame };
      return;
    }
    if (join || table || room) {
      if (room && !join && !table) this.pendingChoice = room;
      else this.pendingJoin = { code: join || table, asTable: Boolean(table) };
      this.wantsOnline = true;
      // Read once, then out of the address bar: left there, a bookmark or a
      // reload carried the code forever and walked the phone back into LAST
      // week's room over the one it was in (playtest, 2026-10-15). A reload
      // finds its room through the remembered room instead.
      params.delete('join');
      params.delete('table');
      params.delete('room');
      const rest = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    }
  },
  mounted() {
    this.onFullscreen = () => { this.fullscreen = Boolean(document.fullscreenElement); };
    document.addEventListener('fullscreenchange', this.onFullscreen);
    // Dev only: the learner (tools/learner) reads where a real join has got to.
    if (import.meta.env.DEV) window.__store = this.$store;
    // A mic that was on before a reload restarts on the next tap; it needs
    // to know who is talking before anyone touches the menu.
    setMicContext(() => this.micContext());
    if (this.previewing) return;
    this.$store.dispatch('initAuth');
    if (!localStorage.getItem(HOWTO_SEEN_KEY) && !this.$store.state.hasSavedGame && !this.pendingJoin?.asTable && !this.pendingChoice) {
      this.showHowTo = true;
    }
    initUpdateCheck(this.$store);
    if (this.isApproved) this.consumeJoin();
    // A saved LOCAL game only resumes when this screen was not in a room -
    // otherwise a refreshed phone would show a frozen copy of an old solo
    // game instead of catching up with the table (playtest 2).
    if (this.$store.state.hasSavedGame && !this.game && !this.roomId && !this.pendingJoin && !this.pendingChoice && !this.rememberedRoom) {
      this.$store.dispatch('resumeGame');
    }
    // iOS Safari scrolls the whole page when a textarea (the bug button's)
    // takes the keyboard and does not always scroll back, leaving the header
    // under the notch and a gap at the bottom. Put it back on blur.
    window.addEventListener('focusout', this.unshift);
  },
  beforeUnmount() {
    window.removeEventListener('focusout', this.unshift);
    document.removeEventListener('fullscreenchange', this.onFullscreen);
  },
  methods: {
    toggleFullscreen() {
      if (document.fullscreenElement) document.exitFullscreen?.();
      else document.documentElement.requestFullscreen?.().catch(() => {});
    },
    // "tutorial" is your own role's; "tutorial:trade" picks one from the
    // menu before a game, when there is no role yet.
    helpPick(key) {
      if (key === 'tutorial' || key.startsWith('tutorial:')) {
        this.helpView = null;
        const role = key.split(':')[1] || this.help.role;
        if (role) this.$store.dispatch('startTutorial', role);
        return;
      }
      this.helpView = key;
    },
    playtestSwitch() { setPlaytestOn(this.roomId, !playtest.on); },
    // Each line carries who said it and where the game was.
    micToggle() {
      if (playtest.mic === 'on' && !this.micArmed) {
        this.micArmed = true;
        clearTimeout(this.micArmTimer);
        this.micArmTimer = setTimeout(() => { this.micArmed = false; }, 2500);
        return;
      }
      clearTimeout(this.micArmTimer);
      this.micArmed = false;
      toggleMic(this.micContext);
    },
    micContext() {
      return (() => {
        const st = this.$store.state;
        const g = st.game;
        return {
          uid: st.user?.uid || null,
          name: st.user?.displayName || 'someone',
          roles: this.$store.getters.myRoles || [],
          looking: helpState.role || (this.isTable ? 'table' : null),
          round: g?.meta?.round ?? null,
          phase: g?.meta?.phase ?? null,
          seq: g?.meta?.seq ?? null,
        };
      })();
    },
    openHelp() {
      // No game on this screen: there is only one thing to read.
      if (!this.game || this.isTable) { this.showHowTo = true; return; }
      this.helpView = 'menu';
    },
    toggleMeasure() {
      this.measured = this.measured ? '' : viewportLine();
    },
    // Hold for the measurement; a tap is a reload.
    stampHold() {
      this.held = false;
      this.holdTimer = setTimeout(() => { this.held = true; this.toggleMeasure(); }, 550);
    },
    stampRelease() {
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.holdTimer = null;
    },
    stampTap() {
      if (this.held) { this.held = false; return; }
      if (this.measured) { this.measured = ''; return; }
      this.refreshing = true;
      forceRefresh();
    },
    dismissHowTo() {
      this.showHowTo = false;
      this.helpView = null;
      try { localStorage.setItem(HOWTO_SEEN_KEY, '1'); } catch { /* storage off */ }
    },
    // The one shared link: this screen is a player board or the table, and
    // from here it is exactly the ?join= or ?table= link.
    chooseScreen(asTable) {
      this.pendingJoin = { code: this.pendingChoice, asTable };
      this.pendingChoice = null;
      if (!asTable && !localStorage.getItem(HOWTO_SEEN_KEY)) this.showHowTo = true;
      this.consumeJoin();
    },
    consumeJoin() {
      // Nothing to join, and no walking back into the last room, until the
      // shared link has been told which screen this is.
      if (this.pendingChoice) return;
      if (this.pendingJoin) {
        const pending = this.pendingJoin;
        this.pendingJoin = null;
        this.$store.dispatch('joinRoomByCode', pending);
        return;
      }
      // No link to follow: walk back into the room this screen was in.
      if (!this.roomId && !this.game) this.$store.dispatch('rejoinRoom');
    },
    unshift() {
      window.setTimeout(() => window.scrollTo(0, 0), 50);
    },
    confirmLeave() {
      const online = Boolean(this.roomId);
      const message = this.isTable
        ? 'Close the table? The game carries on; open it again with the code.'
        : online ? 'Leave this game? The others carry on without you.' : 'Abandon this game?';
      // eslint-disable-next-line no-alert -- a deliberate, single destructive confirm
      if (!window.confirm(message)) return;
      if (online) {
        this.$store.dispatch('leaveRoom');
        this.wantsOnline = false;
      } else {
        this.$store.dispatch('abandonGame');
      }
    },
  },
};
</script>
